import { Box, Flex, Grid, Heading, Link, Table, Text } from '@chakra-ui/react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Container, DisplayHeading, DotPill, Eyebrow } from '@/travo/components/ui/postmark';
import { SEO } from '@/travo/components/SEO';
import { BreadcrumbSchema, WebPageSchema } from '@/shared/seo';
import { ROUTES } from '@/travo/routes';
import { APP_IDS, SITE_URL, SUPPORT_EMAIL } from '@/shared/site';
import { localizePath, useCopy, useLocale, type Locale } from '@/travo/i18n';
import { legalTexts, loadLegalTexts, type LegalBlock, type LegalCopy, type LegalSection } from '@/travo/i18n/legal';
import { INK_RULE } from '@/travo/theme/constants';

const linkStyles = { color: 'stamp', textDecoration: 'underline', textUnderlineOffset: '3px', _hover: { color: 'stampDeep' } } as const;

// **bold**, [label](href) and the support email. Only #, / and http(s) hrefs make a link, so a bracketed
// placeholder such as LEGAL_NAME followed by a CJK '(' stays text.
const INLINE = new RegExp(`(\\*\\*.+?\\*\\*|\\[[^\\]]+\\]\\((?:#|/|https?:)[^)]+\\)|${SUPPORT_EMAIL.replace(/\./g, '\\.')})`);

const InlineLink = ({ href, locale, children }: { href: string; locale: Locale; children: ReactNode }) => {
  if (href.startsWith('#')) return <Link href={href} {...linkStyles}>{children}</Link>;
  if (href.startsWith('/'))
    return (
      <Link asChild {...linkStyles}>
        <RouterLink to={localizePath(locale, href)}>{children}</RouterLink>
      </Link>
    );
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" {...linkStyles}>
      {children}
    </Link>
  );
};

/** Copy text with its inline markup turned into bold, links and mailto links. */
const Rich = ({ text }: { text: string }) => {
  const locale = useLocale();
  return text.split(INLINE).map((part, i) => {
    if (part === SUPPORT_EMAIL)
      return (
        <Link key={i} href={`mailto:${SUPPORT_EMAIL}`} {...linkStyles}>
          {part}
        </Link>
      );
    if (part.startsWith('**') && part.endsWith('**'))
      return (
        <Box as="strong" key={i} fontWeight="700">
          {part.slice(2, -2)}
        </Box>
      );
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link)
      return (
        <InlineLink key={i} href={link[2]} locale={locale}>
          {link[1]}
        </InlineLink>
      );
    return part;
  });
};

const Block = ({ block, legal }: { block: LegalBlock; legal: LegalCopy }) => {

  if (typeof block === 'string')
    return (
      <Text>
        <Rich text={block} />
      </Text>
    );
  if (Array.isArray(block))
    return (
      <Box as="ul" listStyleType="disc" ps="22px" display="flex" flexDirection="column" gap="8px">
        {block.map((item) => (
          <li key={item}>
            <Rich text={item} />
          </li>
        ))}
      </Box>
    );
  if ('h3' in block)
    return (
      <Heading as="h3" fontFamily="body" fontSize="19px" fontWeight="700" lineHeight="1.35" mt="12px">
        {block.h3}
      </Heading>
    );
  if ('inShort' in block)
    return (
      <Text bg="card" borderLeft="4px solid" borderColor="sun" px="16px" py="12px" fontStyle="italic" color="inkSoft">
        <Box as="span" fontFamily="mono" fontStyle="normal" fontSize="13px" letterSpacing="0.08em" color="ink" me="6px">
          {legal.inShort}
        </Box>
        {block.inShort}
      </Text>
    );
  return (
    <Box overflowX="auto" border="2px solid" borderColor="ink" bg="card">
      <Table.Root size="sm" fontSize="14px" minW="560px">
        <Table.Header>
          <Table.Row bg="sand">
            {block.table.head.map((cell) => (
              <Table.ColumnHeader key={cell} color="ink" fontWeight="700">
                {cell}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {block.table.rows.map((row) => (
            <Table.Row key={row[0]} bg="card">
              {row.map((cell, i) => (
                // fixed column order: the index is a stable key
                <Table.Cell key={i} verticalAlign="top">
                  {cell}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

const Contents = ({ sections }: { sections: LegalSection[] }) => (
  <Flex as="ol" direction="column" fontSize="14px" listStyleType="none">
    {sections.map((section) => (
      <li key={section.id}>
        <Link
          href={`#${section.id}`}
          display="block"
          py="6px"
          ps="12px"
          borderLeft="2px solid"
          borderColor="ink/20"
          color="inkSoft"
          textDecoration="none"
          _hover={{ color: 'stamp', borderColor: 'stamp' }}
        >
          {section.heading}
        </Link>
      </li>
    ))}
  </Flex>
);

interface LegalPageProps {
  kind: 'privacy' | 'terms';
}

/** /travo/privacy and /travo/terms in every locale. English governs; translations say so and link to it. */
const LegalPage = ({ kind }: LegalPageProps) => {
  const copy = useCopy();
  const locale = useLocale();
  // Present at build time and on a first visit (main.tsx loads them before hydrating); fetched on client-side navigation
  const [texts, setTexts] = useState(legalTexts);

  useEffect(() => {
    if (texts) return;
    // A failed chunk load: a full page load still gets the prerendered text
    loadLegalTexts().then(setTexts, (error: unknown) => {
      console.error('Could not load the legal texts; reloading the page', error);
      window.location.reload();
    });
  }, [texts]);

  if (!texts) return <Box as="main" minH="100vh" />;
  const legal = texts[locale];
  const doc = legal[kind];
  const path = ROUTES[kind];

  return (
    <>
      <SEO {...doc.seo} path={path} />
      <WebPageSchema
        name={doc.seo.title}
        description={doc.seo.description}
        url={SITE_URL + localizePath(locale, path)}
        inLanguage={copy.htmlLang}
        about={APP_IDS.travo}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Travo', url: SITE_URL + localizePath(locale, ROUTES.home) },
          { name: doc.title, url: SITE_URL + localizePath(locale, path) }
        ]}
      />
      <main>
        <Container py={{ base: '48px', md: '72px' }}>
          <Flex direction="column" gap="18px" maxW="72ch">
            <DotPill>{legal.eyebrow}</DotPill>
            <DisplayHeading as="h1" fontSize={{ base: '40px', md: '56px' }}>
              {doc.title}
            </DisplayHeading>
            <Eyebrow>{doc.updated}</Eyebrow>
            {legal.translationNote && (
              <Text fontSize="15px" color="inkSoft" borderTop={INK_RULE.dashed} pt="14px">
                {legal.translationNote}{' '}
                <Link asChild {...linkStyles}>
                  <RouterLink to={localizePath('en', path)}>{legal.readInEnglish}</RouterLink>
                </Link>
              </Text>
            )}
          </Flex>

          <Grid templateColumns={{ base: 'minmax(0, 1fr)', lg: '260px minmax(0, 72ch)' }} gap={{ base: '32px', lg: '64px' }} mt={{ base: '32px', md: '48px' }}>
            <Box as="nav" aria-label={legal.onThisPage} position={{ lg: 'sticky' }} top="96px" alignSelf="start" maxH={{ lg: 'calc(100vh - 120px)' }} overflowY={{ lg: 'auto' }}>
              <Box as="details" hideFrom="lg" border="2px solid" borderColor="ink" bg="card" px="16px" py="12px">
                <Box as="summary" cursor="pointer" fontFamily="mono" fontSize="13px" letterSpacing="0.14em" textTransform="uppercase">
                  {legal.onThisPage}
                </Box>
                <Box mt="10px">
                  <Contents sections={doc.sections} />
                </Box>
              </Box>
              <Box hideBelow="lg">
                <Eyebrow mb="10px">{legal.onThisPage}</Eyebrow>
                <Contents sections={doc.sections} />
              </Box>
            </Box>

            <Flex as="article" direction="column" gap="16px" lineHeight="1.7" color="ink" overflowWrap="anywhere">
              {doc.sections.map((section, i) => (
                <Flex as="section" key={section.id} id={section.id} direction="column" gap="16px" pt={i ? '28px' : 0} borderTop={i ? INK_RULE.dashed : undefined} mt={i ? '20px' : 0}>
                  <Heading as="h2" fontFamily="body" fontSize={{ base: '22px', md: '26px' }} fontWeight="800" lineHeight="1.3">
                    {section.heading}
                  </Heading>
                  {section.blocks.map((block, j) => (
                    // static list from copy: the index is a stable key
                    <Block key={j} block={block} legal={legal} />
                  ))}
                </Flex>
              ))}
            </Flex>
          </Grid>
        </Container>
      </main>
    </>
  );
};

export default LegalPage;
